import {
  ImportExportIOJobStatus,
  ImportExportRecordImportMode,
  type ImportExportRenderedError,
} from '@robovision/import-export'
import { HttpStatusCode } from '@robovision/rvai-utils'
import axios, { type AxiosProgressEvent } from 'axios'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import {
  reactive,
  toRefs,
} from 'vue'
import { ApiErrorId } from '~domain/ApiErrorId'
import {
  ImportJob,
  ImportJobStatus,
} from '~domain/io/ImportJob'
import {
  dataApi,
  importExportApi,
} from '~services/api'
import { acceptStatus } from '~utils/api'
import { handleEvent } from '~utils/events'
import { useTagsStore } from './useTagsStore'


interface JobCreation {
  jobId: string
}

interface JobUpdate {
  amountDone: number
  amountFailed: number
  amountTotal?: number
  dataFormat?: string
  jobId: string
  jobStatus: ImportExportIOJobStatus
  jobStatusAttributes?: {
    rollback: boolean
  }
}

interface StartImportJob {
  datapoolId: string
  file: File | Promise<File>
  importMode?: ImportExportRecordImportMode
  setEventSource?: boolean
  tag: string
}

interface StartImportJobBackground {
  datapoolId: string
  file: File | Promise<File>
  importId: string
  importMode?: ImportExportRecordImportMode
  setEventSource?: boolean
}

interface UpdateTagNameParams {
  importId: string
  name: string
  projectId: string
}

export const useImportJobsStore = defineStore('importJobs', () => {
  const state = reactive({
    // map (actual) job ids to event sources
    eventSources: new Map<string, EventSource>(),
    imports: new Map<string, ImportJob>(),
  })

  const getters = {
    getImportJob (importId: string): ImportJob | undefined {
      return state.imports.get(importId)
    },

    getImportJobByJobId (jobId: string): ImportJob | undefined {
      return [...state.imports.values()].find(job => job.jobId === jobId)
    },

    hasUploadsInProgress (): boolean {
      return [...state.imports.values()]
        .some(job => job.status === ImportJobStatus.UPLOADING)
    },

    isFailed (importId: string): boolean {
      switch (state.imports.get(importId)?.status) {
        case ImportJobStatus.FAILED:
        case ImportJobStatus.INCOMPATIBLE_FORMAT:
        case ImportJobStatus.KILLED:
          return true
        default:
          return false
      }
    },

    isInProgress (importId: string): boolean {
      switch (state.imports.get(importId)?.status) {
        case undefined:
        case ImportJobStatus.UPLOADING:
        case ImportJobStatus.STARTING:
        case ImportJobStatus.RUNNING:
        case ImportExportIOJobStatus.STOPPING:
          return true
        default:
          return false
      }
    },
  }

  const actions = {
    async cancelImportJob (id: string, rollback = false): Promise<void> {
      // id is importId or jobId
      const job = state.imports.get(id) || [...state.imports.values()].find(i => i.jobId === id)
      if (!job) return

      if (job.abortController) {
        job.abortController.abort()
      }

      if (job.jobId) {
        await importExportApi.imports.stopImportJob(job.jobId, { rollback })
      }

      job.status = ImportJobStatus.STOPPING
    },

    closeSse (jobId: string): void {
      state.eventSources.get(jobId)?.close()
      setTimeout(() => state.eventSources.delete(jobId), 0)
    },

    async deleteImportJob (importId: string): Promise<void> {
      const job = state.imports.get(importId)
      if (!job) return

      if (job.jobId) {
        await importExportApi.imports.deleteImportJob(job.jobId)
      }

      state.imports.delete(importId)
    },

    async handleJobCreationEvent ({ jobId }: JobCreation, datapoolId: string): Promise<void> {
      try {
        const importJob = await this.loadImportJob(jobId)
        if (!importJob?.jobId) return

        // sometimes we receive job creation event before startImportJob response
        const waitingImportJobs = Array.from(state.imports.values())
          .filter(x => x.status === ImportJobStatus.UPLOADING && x.uploadProgress === 1)

        // we don't have any information about jobId for imports in the waiting status
        // and imports are duplicated in the store
        // therefore, we are waiting for the startImportJob to be resolved
        if (waitingImportJobs.length) {
          setTimeout(() => {
            void this.handleJobCreationEvent({ jobId }, datapoolId)
          }, 1000)

          return
        }
        if (!state.imports.has(importJob.importId)) {
          state.imports.set(importJob.importId, importJob)
        }

        if (!importJob.isFinished && !state.eventSources.has(importJob.jobId)) {
          state.eventSources.set(
            jobId,
            this.initializeSse(jobId, jobId),
          )
        }
      } catch (e) {
        console.error(e)
      }
    },

    async handleJobUpdate (message: JobUpdate, importId: string): Promise<void> {
      const job = await (state.imports.get(importId) || this.loadImportJob(message.jobId))
      if (!job) return

      job.status = message.jobStatus
      job.dataFormat = message.dataFormat
      job.amountDone = message.amountDone
      job.amountFailed = message.amountFailed
      job.amountTotal = message.amountTotal

      // Update sampleCount to match amountSuccess
      job.sampleCount = job.amountSuccess

      if (message.jobStatus !== ImportExportIOJobStatus.STARTING &&
        message.jobStatus !== ImportExportIOJobStatus.RUNNING &&
        message.jobStatus !== ImportExportIOJobStatus.STOPPING &&
        job.jobId
      ) {
        this.closeSse(job.jobId)
      }

      if (message.jobStatus === ImportExportIOJobStatus.STOPPED && message.jobStatusAttributes?.rollback) {
        state.imports.delete(importId)
      }
    },

    initializeImportsCreationSse (datapoolId: string): EventSource {
      const source = new EventSource('/io/api/v1/imports/-/creation')

      source.addEventListener('JobCreation', handleEvent((e: JobCreation) => this.handleJobCreationEvent(e, datapoolId)))

      return source
    },

    initializeSse (importId: string, jobId: string): EventSource {
      const eventSource = new EventSource(`/io/api/v1/imports/${jobId}/updates`)
      // TODO: handle export samples job
      // const eventSource = new EventSource(`/io/api/v1/jobs/${jobId}/updates`)

      eventSource.addEventListener('JobUpdate', handleEvent((e: JobUpdate) => this.handleJobUpdate(e, importId)))
      eventSource.addEventListener('error', () => {
        console.error('An error occurred while attempting to initializeSse.')
      })

      return eventSource
    },

    async loadImportJob (jobId: string): Promise<ImportJob | undefined> {
      const importResponse = await importExportApi.imports.getImportJob(
        jobId,
        { validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND) },
      )

      if (
        !importResponse?.data ||
        (importResponse.data as unknown as ImportExportRenderedError).id === ApiErrorId.JobNotFound
      ) {
        return
      }

      const { data: job } = importResponse
      const { data: { tags } } = await dataApi.pools.listPoolTags(job.datapoolId, { limit: null })

      const tag = tags.find(x => x.tagId === job.tagId)
      if (!tag) return

      if (!state.eventSources.has(job.jobId)) {
        state.eventSources.set(
          job.jobId,
          this.initializeSse(job.jobId, job.jobId),
        )
      }

      const importJob = ImportJob.fromResponse(job, tag)

      // When we start import we create a fake import and using uuid as import id
      // here we remove fake import when receive a real one
      const fakeImport = [...state.imports.values()]
        .find(i => i.jobId === importJob.jobId && i.importId !== importJob.importId)

      if (fakeImport) {
        state.imports.delete(fakeImport.importId)
      }
      state.imports.set(importJob.importId, importJob)

      return importJob
    },

    async loadImportJobs (datapoolId: string, limit?: number): Promise<string[]> {
      const { data: { imports: jobs } } = await importExportApi.imports.listImportJobs({
        filter: [
          `datapoolId:${datapoolId}`,
        ],
        ...(limit ? { limit } : {}),
        sort: ['-createdAt'],
      } as never) // TODO should be fixed in the generated client

      const { data: { tags } } = await dataApi.pools.listPoolTags(datapoolId, { limit: null })

      // Delete from store all datapool import jobs
      const stateImportJobs = [...state.imports.values()]
      stateImportJobs.forEach(j => {
        if (j.datapoolId === datapoolId && j.status !== ImportJobStatus.UPLOADING) {
          state.imports.delete(j.importId)
        }
      })

      for (const job of jobs) {
        const tag = tags.find(x => x.tagId === job.tagId)
        if (!tag) {
          // skipping, tag was most likely deleted
          const importId = [...state.imports.values()].find(j => j.jobId === job.jobId)?.importId
          if (importId) {
            state.imports.delete(importId)
          }
          continue
        }

        // if there is a previously loaded import job, use its importId
        const prevJob = [...state.imports.entries()].find(([, j]) => j.jobId === job.jobId)
        const importId = prevJob?.[0]

        const importJob = ImportJob.fromResponse(job, tag, importId)
        state.imports.set(importJob.importId, importJob)
      }

      // monitor updates
      const inProgress = jobs.filter(x => x.jobStatus === ImportExportIOJobStatus.STARTING ||
        x.jobStatus === ImportExportIOJobStatus.RUNNING ||
        x.jobStatus === ImportExportIOJobStatus.STOPPING)
      for (const job of inProgress) {
        if (!state.eventSources.has(job.jobId)) {
          state.eventSources.set(
            job.jobId,
            this.initializeSse(job.jobId, job.jobId),
          )
        }
      }

      return jobs.map(x => x.jobId)
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    startImportJob ({
      datapoolId,
      file,
      importMode = ImportExportRecordImportMode.ANY,
      setEventSource = false,
      tag,
    }: StartImportJob): string {
      const importJob = new ImportJob({
        datapoolId,
        importId: uuidv4(),
        name: tag,
      })
      state.imports.set(importJob.importId, importJob)

      void this.startImportJobBackground({
        datapoolId,
        file,
        importId: importJob.importId,
        importMode,
        setEventSource,
      })

      return importJob.importId
    },

    async startImportJobBackground (
      {
        datapoolId,
        file,
        importId,
        importMode,
        setEventSource = false,
      }: StartImportJobBackground,
    ): Promise<void> {
      const job = state.imports.get(importId)
      if (!job) throw new Error('Invalid import id')

      try {
        job.status = ImportJobStatus.UPLOADING
        job.abortController = new AbortController()
        job.uploadProgress = 0

        const { data } = await importExportApi.imports.startImportJob({
          file: [await file],
          parameters: {
            datapoolId,
            importMode,
            tagName: job.name,
          },
        }, {
          onUploadProgress: (data: AxiosProgressEvent) => {
            const pct = data.loaded / (data.total ?? 0)
            job.uploadProgress = pct
          },
          signal: job.abortController.signal,
        })
        job.jobId = data.jobId
        job.tagId = data.tagId
        job.abortController = undefined

        if (!state.eventSources.has(data.jobId) && setEventSource) {
          state.eventSources.set(
            data.jobId,
            this.initializeSse(job.importId, data.jobId),
          )
        }
      } catch (e) {
        if (axios.isCancel(e)) {
          console.warn('Canceled by the user')
          state.imports.delete(importId)
        } else if (axios.isAxiosError(e)) {
          job.status = ImportJobStatus.FAILED
          // TODO handle with the error library
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          job.error = (e.response?.data as any)?.message
          console.error(e)
        } else {
          // TODO handle
          job.status = ImportJobStatus.FAILED
          console.error(e) // TODO add to job
        }
      }
    },

    async updateTagName ({
      importId,
      name,
      projectId,
    }: UpdateTagNameParams): Promise<void> {
      const { updateTagName } = useTagsStore()

      const job = state.imports.get(importId)
      if (!job?.tagId) return

      await updateTagName(projectId, job.tagId, name)
      job.name = name
    },
  }

  return {
    ...toRefs(state),
    ...getters,
    ...actions,
  }
})
